/**
 * Thin, typed wrappers around `window.gtag` for the Google Analytics 4 events
 * theaWeb fires. The gtag.js snippet itself is loaded by `public/index.html`;
 * these helpers exist for the same reasons as `metaPixel.ts`:
 *
 *   - Centralize the "is gtag actually loaded yet?" + bot-detection guards so
 *     callers don't sprinkle the same `typeof` check at every fire site.
 *   - Give each event a typed param interface so we don't quietly drift the
 *     vocabulary across surfaces.
 *   - Defer every fire to `requestIdleCallback` so analytics never blocks
 *     paint, INP, or LCP (analytics handoff §16 perf budget rule #1).
 *   - Keep params PII-free (no name, email, uid, recipientId).
 *
 * Property ID G-KV1K3W6CLJ (set in public/index.html). Reused from the old
 * givethea.com codebase so historical funnel data stays continuous through
 * the DNS cutover.
 *
 * Custom event names use snake_case to match GA4 convention. `select_item` is
 * GA4's standard recommended event for "user clicked a product card" and is
 * the analogue of Meta's `ViewContent`.
 */
import { isSuppressedSession } from '../auth/impersonation';
import { isAnalyticsGranted } from '../consent/consentStore';
import { isBot } from './botDetect';
import { fireWhenIdle } from './idleCallback';
import { scrubSensitiveParams } from './sensitiveUrlParams';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * May we emit a GA4 hit right now?
 *
 * The consent term is the banner gate. Note it is DEFENCE IN DEPTH on
 * top of Google Consent Mode v2: `public/index.html` already sets
 * `analytics_storage: 'denied'` before gtag.js loads, so a hit that slipped
 * through would be cookieless anyway. This check means no hit is emitted by
 * our code at all until the visitor accepts — which is what the requirement
 * literally asks for ("GA4 ... doesn't fire on load until user clicks accept").
 *
 * gtag.js itself still loads, deliberately: that is the shape Consent Mode is
 * designed for, and it means an accept click takes effect immediately instead
 * of waiting on a script download.
 */
function canFire(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.gtag !== 'undefined' &&
    !isBot() &&
    // Admin impersonation sessions (?becomeToken=) and test accounts
    // (`testUser` claim) emit nothing — their hits would be stamped with a
    // user_id/thea_uid whose analytics they would pollute.
    !isSuppressedSession() &&
    isAnalyticsGranted()
  );
}

/**
 * The URL scrub policy — which query params may never reach an analytics
 * property — now lives in `lib/sensitiveUrlParams.ts`, because it is not a GA
 * concern: `lib/metaCapi.ts` needs the same answer for `event_source_url`, and
 * while this policy was reachable only by importing the GA module, the Meta
 * path shipped the raw href instead (THE-765 / PR 3.4).
 *
 * Re-exported here so every existing importer keeps working unchanged.
 */
export { scrubSensitiveParams };

/**
 * @deprecated Use `scrubSensitiveParams`. Retained so existing imports keep
 * working; it now strips the OTP params too, not only the resume token.
 */
export const scrubResumeToken = scrubSensitiveParams;

/** The current page URL with sensitive params stripped — for GA4's
 *  `page_location`. Returns undefined off-window (SSR) so `emit` omits the key. */
function currentScrubbedLocation(): string | undefined {
  if (typeof window === 'undefined' || !window.location) return undefined;
  return scrubSensitiveParams(window.location.href);
}

/**
 * A scrubbed `page_referrer` — GA4's `dr` — but ONLY when scrubbing actually
 * changed `document.referrer`. Returns undefined otherwise, so `emit` omits the
 * key and gtag computes `dr` exactly as it does today.
 *
 * THE-790, and the narrowness is the point. The observed `dr` leak was gtag's
 * own init-time href snapshot being replayed as the session's "previous page";
 * `public/index.html`'s erase closes that at the source, because the snapshot is
 * taken from an already-clean URL. This is the residual: a REAL cross-document
 * referrer, where the browser — not gtag — hands us a previous URL that carries
 * a sensitive param. THE-765's scrub owns `page_location` only and has never had
 * any reach over `dr`.
 *
 * Why conditional rather than always stamping the referrer. Supplying
 * `page_referrer` unconditionally would override gtag's SPA previous-page
 * tracking on every hit, replacing a correct in-session value with the original
 * document referrer and silently rewriting acquisition and page-path reporting.
 * That is a reporting change, not a privacy fix. Omitting the key unless the
 * value is dirty buys the defence at zero behavioural cost.
 */
function currentScrubbedReferrer(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const raw = document.referrer;
  if (!raw) return undefined;
  const scrubbed = scrubSensitiveParams(raw);
  return scrubbed === raw ? undefined : scrubbed;
}

/**
 * Stamp a scrubbed `page_location` onto every event's params. gtag.js otherwise
 * auto-attaches the raw `document.location.href` as `dl` on EVERY hit — which
 * leaks `rt` / `otp_email` to GA4. Setting `page_location` here overrides that
 * auto `dl` centrally, so no individual fire site can leak. A caller that
 * supplies its own `page_location` (page_view) still wins — it scrubs the same.
 *
 * `page_referrer` rides along on the same chokepoint when, and only when, the
 * real referrer is dirty (see `currentScrubbedReferrer`). Both keys go FIRST so
 * a caller's explicit value still wins.
 */
function withScrubbedLocation(
  params: Record<string, unknown>,
): Record<string, unknown> {
  const loc = currentScrubbedLocation();
  const ref = currentScrubbedReferrer();
  return {
    ...(loc === undefined ? {} : { page_location: loc }),
    ...(ref === undefined ? {} : { page_referrer: ref }),
    ...params,
  };
}

/**
 * Inner fire — the single chokepoint every GA4 event hit flows through. Skips
 * bots and un-loaded gtag, and stamps the scrubbed page_location on the hit.
 */
function emit(eventName: string, params: Record<string, unknown>): void {
  if (!canFire()) return;
  window.gtag?.('event', eventName, withScrubbedLocation(params));
}

// --- Page view --------------------------------------------------------------

export type PageType =
  | 'home'
  | 'quiz'
  | 'results'
  | 'guide'
  /** The /gift-guides browse index (distinct from an individual guide). */
  | 'guide_index'
  | 'board'
  /** Public /b/:shareId boards. */
  | 'shared_board'
  /** The saved views: /liked and /liked/:recipientId. */
  | 'saved'
  /** The /you account page. */
  | 'account'
  | 'calendar'
  | 'other';

export interface GaPageViewParams {
  /** Coarse classification of the route. Drives the dashboard's "% sessions on
   *  guide" and "quiz funnel" filters. */
  page_type: PageType;
  /** Set on guide pages (slug, e.g. `mothers_day`). Undefined elsewhere. */
  occasion?: string;
  /** Per-session counter of guide visits. 1 on first guide hit, 2 on second... */
  guide_visit_number?: number;
  /**
   * Override for `page_location`/`page_path` (e.g. `/guide/mothers_day`).
   * `usePageTracking`'s route-change calls never pass this — they stay on
   * `window.location`, unchanged. THE-1394's pop-up page view needs it: the
   * address bar still reads the board/hub URL underneath, but the page view
   * must point at the guide, same as if the shopper had navigated there.
   */
  page_path?: string;
  /**
   * GA4's `page_title`. Optional and omitted from the payload when unset, so
   * every page view that doesn't pass it stays byte-identical to today's
   * (`gaPixel.test.ts` pins the exact payload).
   */
  page_title?: string;
}

/**
 * Standard GA4 page_view. Fired by `usePageTracking` on every route change,
 * and directly by `DiveDeeperGuideOverlay` for a guide shown in its pop-up
 * (THE-1394) — a surface that never navigates, so there is no route change
 * for `usePageTracking` to see.
 */
export function gaPageView(params: GaPageViewParams): void {
  fireWhenIdle(() => {
    const overridePath =
      params.page_path !== undefined ? scrubSensitiveParams(params.page_path) : undefined;
    const pageLocation =
      overridePath !== undefined
        ? typeof window !== 'undefined'
          ? `${window.location.origin}${overridePath}`
          : overridePath
        : typeof window !== 'undefined'
          ? scrubSensitiveParams(window.location.href)
          : '';
    const pagePath =
      overridePath !== undefined
        ? overridePath
        : typeof window !== 'undefined'
          ? scrubSensitiveParams(window.location.pathname + window.location.search)
          : '';
    emit('page_view', {
      page_location: pageLocation,
      page_path: pagePath,
      page_type: params.page_type,
      ...(params.occasion !== undefined ? { occasion: params.occasion } : {}),
      ...(params.guide_visit_number !== undefined
        ? { guide_visit_number: params.guide_visit_number }
        : {}),
      ...(params.page_title !== undefined ? { page_title: params.page_title } : {}),
    });
  });
}

// --- User identity ---------------------------------------------------------

const GA_PROPERTY_ID = 'G-KV1K3W6CLJ';

/**
 * Wire the Firebase UID into GA4 via TWO separate channels. Both are needed;
 * neither alone is sufficient for end-to-end per-user analysis.
 *
 *   1. `gtag('config', GA_ID, { user_id })` — GA4's built-in User-ID feature.
 *      Used by GA4 internally to stitch sessions across devices and time for
 *      the same person. NOT exposed as a queryable custom dimension (the
 *      property name `user_id` is reserved by GA4).
 *
 *   2. `gtag('set', 'user_properties', { thea_uid })` — sets a custom
 *      user-scoped property which IS queryable as `customUser:thea_uid` in
 *      the Data API once registered as a custom dimension in GA4 Admin.
 *      The dimension was registered 2026-05-05 with property name `thea_uid`.
 *
 * Why `thea_uid` and not e.g. `firebase_uid`? GA4 reserves these prefixes:
 *   - `firebase_*` (Firebase SDK integration)
 *   - `google_*`   (Google integrations)
 *   - `ga_*`       (GA4 internal)
 *   - `_*`         (leading underscore — GA4 internal)
 * Custom dimensions on those prefixes get rejected with "User property name
 * is not allowed." See `~/git/thea/.claude/rules/gotchas.md` for the full
 * trap explanation.
 *
 * Re-firing for the SAME uid is idempotent at gtag, but the caller (the
 * `useGaUserIdentity` hook) still de-dups on uid change to avoid extra work.
 *
 * IMPORTANT: passing `null` clears BOTH user_id and thea_uid (e.g. on
 * signOut). Without this, GA4 would continue stamping subsequent events
 * with the previous UID even after auth state cleared — leaking the prior
 * user's identifier across browsing sessions on shared devices.
 *
 * `send_page_view: false` mirrors the initial config in `public/index.html`
 * so this re-config never accidentally fires a phantom page_view on every
 * auth state change (auth resolves can re-fire several times per session).
 */
export function gaSetUserId(uid: string | null): void {
  if (typeof window === 'undefined' || typeof window.gtag === 'undefined') return;
  // A user_id IS a cross-session identifier, so it is gated on consent like
  // any other analytics hit. Clearing it (uid === null) is always allowed —
  // never leave a stale identifier configured because consent lapsed.
  // An impersonation or test session must never attach its uid as a GA
  // user_id either (same posture as canFire's gate).
  if (uid !== null && (!isAnalyticsGranted() || isSuppressedSession())) return;
  window.gtag('config', GA_PROPERTY_ID, {
    user_id: uid,
    send_page_view: false,
  });
  window.gtag('set', 'user_properties', {
    thea_uid: uid,
  });
}

/** Firebase Auth provider IDs we expect at the signup boundary. Since #291
 *  (OTP-only sign-in; OAuth providers removed) the only reachable values are
 *  'password' (email-OTP custom token surfaces the email provider) and
 *  'unknown'. Historical GA rows also carry google.com / apple.com. */
export type SignUpMethod = 'password' | 'unknown';

export interface GaSignUpParams {
  /** Firebase provider id captured from `user.providerData[0].providerId`. */
  method: SignUpMethod | string;
}

/**
 * Standard GA4 `sign_up` event. Fired exactly once per Firebase UID at the
 * moment the user transitions from anonymous-or-null to a real account.
 *
 * The detection lives in `useGaUserIdentity`, NOT in any individual signup
 * UI handler — there are multiple entry points (heart-tap modal, header
 * button, sticky-footer button, mobile Google redirect callback) and
 * instrumenting each one risks (a) missing future entry points and (b)
 * double-firing when handlers chain through one another. Watching the auth
 * state transition catches every path for free.
 */
export function gaSignUp(params: GaSignUpParams): void {
  fireWhenIdle(() => emit('sign_up', { ...params }));
}

// --- Quiz funnel ------------------------------------------------------------

/** Surfaces that initiate quiz entry. Spec §11.1. The moms_* / sticky_moms
 *  values are the /welcome2 homepage variant's counterparts of the
 *  homepage_* / sticky_homepage surfaces (working-moms campaign cohort
 *  split). 2026-08-04 parity audit: added the values live navigation was
 *  already sending untyped via router state ('guide', 'shared_board',
 *  'calendar_empty_state') plus 'add_person_tile'; removed 'search_pill'
 *  (the homepage SearchPill was orphaned by the #294 homepage redesign).
 *  sticky_homepage / sticky_moms are currently unreachable — the homepage
 *  renders StickyPrimaryCta with hidePrimaryCta — but stay in the union
 *  because the wiring is intact and the PM may re-enable the button. */
export type QuizEntryPoint =
  | 'homepage_hero'
  | 'homepage_banner'
  | 'sticky_homepage'
  | 'moms_hero'
  | 'moms_banner'
  | 'sticky_moms'
  | 'sticky_occasion'
  | 'banner_mothers_day'
  /** Any /guide/* page's sticky bar / end banner / save-picker CTA. */
  | 'guide'
  /** Public /b/:shareId board's "Start quiz" CTA. */
  | 'shared_board'
  /** Calendar empty state's "+ Add a person". */
  | 'calendar_empty_state'
  /** Homepage "Your people" section's dashed add tile. */
  | 'add_person_tile'
  | 'board_redirect'
  | 'direct';

export interface GaQuizStartParams {
  entry_point: QuizEntryPoint;
  /** Set when entry_point is sticky_occasion or banner_mothers_day. */
  occasion?: string;
}

/** Fired when the user enters the quiz funnel (route mount or SearchPill). */
export function gaQuizStart(params: GaQuizStartParams): void {
  fireWhenIdle(() => emit('quiz_start', { ...params }));
}

/**
 * The quiz steps, in funnel order. Branchy in practice — `gender`/`lifeStage`
 * appear only on some relationship paths, and `dates` only for kids or
 * birthdays — so a given session reaches a SUBSET, always forward.
 */
export type GaQuizStepName =
  | 'relationship'
  | 'lifeStage'
  | 'gender'
  | 'age'
  | 'dates'
  | 'occasion'
  | 'interests'
  // PRD names this page "more" (quiz_step_advanced); the internal step key
  // stays `note` — that's the component/state name, not the analytics one.
  | 'more'
  | 'name'
  | 'freshFindFridays';

export interface GaQuizStepAdvancedParams {
  /** The step the user just REACHED. */
  step_name: GaQuizStepName;
}

/**
 * Custom event — fired once per distinct quiz step the user reaches, in order.
 * Today GA4 only sees `quiz_start` (entered /quiz) and `quiz_search_submitted`
 * (finished every step); the drop-off between them is invisible. This exposes
 * WHICH step leaks: the count falling between two consecutive `step_name`s.
 *
 * Fire-on-arrival, deduped per session — so back-navigation does NOT re-fire
 * (the funnel stays forward-only) and a skipped step simply never fires.
 * Register `step_name` as a GA4 Event-scoped custom dimension to query it.
 */
export function gaQuizStepAdvanced(params: GaQuizStepAdvancedParams): void {
  fireWhenIdle(() => emit('quiz_step_advanced', { ...params }));
}

export interface GaQuizSkipParams {
  /** The step the user deliberately left BLANK. */
  step_name: GaQuizStepName;
}

/**
 * Custom event — the user advanced past an optional step without answering it.
 *
 * WHY THIS IS NOT `quiz_step_advanced`. That event only ever says "the user
 * reached the next step", which is equally true of a filled-in birthday and a
 * skipped one, so the skip rate on the optional steps was invisible in GA4:
 * both taps on the birthday step landed on the same handler and emitted the
 * same event. `quiz_skip` is the deliberate-blank signal, fired IN ADDITION to
 * the forward-only funnel event, so the existing step counts are unchanged and
 * `quiz_skip / quiz_step_advanced` for the same `step_name` reads as the skip
 * rate for that step.
 *
 * Register `step_name` (already an Event-scoped custom dimension for
 * `quiz_step_advanced`) to query it — no new dimension slot is spent.
 */
export function gaQuizSkip(params: GaQuizSkipParams): void {
  fireWhenIdle(() => emit('quiz_skip', { ...params }));
}

/** `shown` on each showing; one of three resolving actions on the dismiss
 *  path (`continue` is the fourth, non-dismiss resolution). The PRD treats
 *  the button, tap-outside and Escape as equivalent user INTENT (all three
 *  land back on the note box), but kept apart in analytics rather than
 *  folded into one `add_details` — otherwise a tap on the actual button is
 *  indistinguishable from an accidental tap-outside or a reflexive Escape. */
export type GaQuizSkipSheetAction =
  | 'shown'
  | 'continue'
  | 'add_details'
  | 'escape'
  | 'tap_outside';

export interface GaQuizSkipSheetParams {
  action: GaQuizSkipSheetAction;
}

/**
 * Custom event — the heads-up sheet the note step raises instead of silently
 * advancing on an empty note.
 *
 * Fires once per SHOWING (`shown`) and once per RESOLVING action (`continue`
 * when the user accepts the general-picks tradeoff; `add_details` /
 * `escape` / `tap_outside` for the three equivalent dismiss paths).
 * `continue + add_details + escape + tap_outside` sums to `shown` for a user
 * who resolves every sheet, so an unresolved sheet is the difference rather
 * than a missing event.
 *
 * Register `action` as a GA4 Event-scoped custom dimension to query it.
 */
export function gaQuizSkipSheet(params: GaQuizSkipSheetParams): void {
  fireWhenIdle(() => emit('quiz_skip_sheet', { ...params }));
}

/** The note step's four length-based encouragement bands (THE-888),
 *  mirrored here rather than imported from `noteEncouragement.ts` so this
 *  analytics module stays free of the quiz's component-tree deps. */
export type GaQuizNoteTier = 'start' | 'better' | 'good' | 'excellent';

export interface GaQuizNoteContinueParams {
  /** The encouragement band the note was in at the moment Continue (or the
   *  skip sheet's "Continue anyway") fired. */
  note_tier: GaQuizNoteTier;
}

/**
 * Custom event — fired once, leaving the note step. PRD-listed alongside
 * `quiz_step_advanced` for the "more" page: that event says the user reached
 * the step, this says how much they wrote before moving on, bucketed to the
 * same four bands the on-screen dial shows them.
 */
export function gaQuizNoteContinue(params: GaQuizNoteContinueParams): void {
  fireWhenIdle(() => emit('quiz_note_continue', { ...params }));
}

export type QuizFlowType = 'first_time' | 'new_recipient' | 'existing_recipient';

/**
 * Custom event — fired exactly once when the React tree first commits a
 * frame to the DOM. Distinguishes "page literally never rendered" (no
 * event) from "page rendered but user bounced" (event fires). Critical
 * for bounce attribution: a Meta-reported "Landing Page View" without a
 * matching `page_first_render` indicates the React app failed to mount,
 * which happens in some IG/FB webviews.
 */
/**
 * A resume token successfully restored an anon session after an in-app
 * browser (IG/FB webview) wiped local auth storage. `surface` is an existing
 * registered dimension — no new GA4 custom-dimension slot spent.
 */
export function gaSessionResume(params: { surface: string }): void {
  fireWhenIdle(() => emit('session_resume', { surface: params.surface }));
}

// --- Email capture (soft board-save, tranche 2) -----------------------------
//
// The board-ready capture sheet's lifecycle. `surface` is the existing
// registered dimension (value 'board' here) — no new GA4 custom-dimension slot
// spent. `email_capture_submitted` is deliberately PII-free (no email, no uid):
// it counts opt-ins for the prompt-shown → submit rate, nothing more.

export interface GaEmailCaptureParams {
  /** Which surface the capture prompt fired on — 'board' for the results page. */
  surface: string;
}

/** The capture sheet was shown (board-ready + dwell / first swipe trigger). */
export function gaEmailCaptureShown(params: GaEmailCaptureParams): void {
  fireWhenIdle(() => emit('email_capture_shown', { surface: params.surface }));
}

/** The user submitted their email from the capture sheet. */
export function gaEmailCaptureSubmitted(params: GaEmailCaptureParams): void {
  fireWhenIdle(() => emit('email_capture_submitted', { surface: params.surface }));
}

/** The user dismissed the capture sheet without submitting (swipe / tap-out). */
export function gaEmailCaptureDismissed(params: GaEmailCaptureParams): void {
  fireWhenIdle(() => emit('email_capture_dismissed', { surface: params.surface }));
}

/**
 * Sign-in modal opened. Tops the auth funnel above `otp_requested` — without
 * it, "how many people even open the door, and from which door?" was
 * invisible (every sign-in CTA already carried a `data-signin-entry`
 * attribute that nothing read). `entry` reuses those attribute values
 * (homepage-header, header-pill, you-page, mark-purchased, …); 'unknown' for
 * programmatic opens that predate the audit. Fired from the single
 * `requestSignIn` chokepoint in AuthGateContext, mirroring the sign_up
 * philosophy of instrumenting the transition, not each button.
 * Register `entry` as a GA4 Event-scoped custom dimension to query it.
 */
export function gaSignInOpened(params: { entry: string; mode: string }): void {
  const { entry, mode } = params;
  fireWhenIdle(() => emit('sign_in_opened', { entry, mode }));
}

/* --- Fresh Find Fridays Unlock funnel (THE-746 Phase 4, PR 4.5) ------------
 *
 * Three events for the no-account Unlock pill: the tap, and the two ways the
 * auth it opens can END. The outcomes are SEPARATE EVENT NAMES rather than one
 * event with an `account` param, because GA4 counts names natively — so "how
 * many new accounts did the pill produce, as against people who already had
 * one?" is answerable in a report with no custom dimension registered.
 *
 * `entry` is the `fresh_find_fridays` string (verbatim, see
 * freshFind/freshFindUnlockFunnel.ts) and rides all three so the funnel reads
 * end to end next to `sign_in_opened`, which carries the same value from the
 * `requestSignIn` chokepoint. Register `entry` as an Event-scoped custom
 * dimension to split by door; the counts work without it.
 *
 * PII-free, and holiday-free: nothing here carries a date, a holiday id, or a
 * recipient (the THE-746 privacy ground rule).
 */

export interface GaFreshFindUnlockParams {
  /** The sign-in door — `fresh_find_fridays`. */
  entry: string;
}

/** The no-account viewer tapped Unlock. Tops this funnel. */
export function gaFreshFindUnlockClick(params: GaFreshFindUnlockParams): void {
  fireWhenIdle(() => emit('fresh_find_unlock_click', { entry: params.entry }));
}

/** An Unlock tap ended in a BRAND-NEW account. */
export function gaFreshFindUnlockAccountCreated(
  params: GaFreshFindUnlockParams,
): void {
  fireWhenIdle(() =>
    emit('fresh_find_unlock_account_created', { entry: params.entry }),
  );
}

/** An Unlock tap ended in a sign-in to an account that already existed. */
export function gaFreshFindUnlockSignedIn(
  params: GaFreshFindUnlockParams,
): void {
  fireWhenIdle(() =>
    emit('fresh_find_unlock_signed_in', { entry: params.entry }),
  );
}

/**
 * THE-1298 — the 10th-reaction "{Name}, decoded" moment that replaces the
 * signed-in toast for a permanent-account viewer with Fresh Find Fridays on.
 * One event, `action` tells the outcome — X/Esc/scrim all count as `close`,
 * same as the spec's "Behaviour" section groups them; Okay is `ok`, its own
 * distinct outcome (Kate, 2026-10-28 scope change: Okay only closes the
 * moment, there is no early-drop regenerate path yet). No PII: no recipient
 * id, no name.
 *
 * THE-1299 widens the same moment (and the same event) to the no-account
 * audience, whose footer swaps Okay for "🔒 Sign in" (`sign_in`) plus a
 * secondary "Later" (`later`) — X/Esc/scrim still report `close` for this
 * audience too, unchanged.
 */
export type FreshFindMomentAction = 'shown' | 'ok' | 'close' | 'sign_in' | 'later';

export function gaFreshFindMoment(params: { action: FreshFindMomentAction }): void {
  fireWhenIdle(() => emit('fresh_find_moment', { surface: 'board', action: params.action }));
}

/**
 * Auth completed (login-signin-or-create-account-v4-spec §5). The VERIFIED
 * outcome of a direct sign-in / create-account through the universal modal —
 * account created vs. an existing sign-in, and what happened to any guest board.
 * Fires for every completed auth, including an existing-account sign-in that
 * shows no confirmation toast, so the funnel sees the whole picture.
 *
 * PII-free by contract: no email, no board name, no provider account id — only
 * the three categorical properties below. Register `entry_context`,
 * `account`, and `guest_data` as GA4 Event-scoped custom dimensions to query
 * them (see the spec's §5 — do this before the production deploy). The spec's
 * `auth_opened` maps to the existing `sign_in_opened` above rather than a
 * duplicate metric; `auth_guest_merge_failed` has no trigger in the v4 normal
 * door (no merge path — the One Rule), so it is intentionally not emitted.
 */
export function gaAuthCompleted(params: {
  entryContext: string;
  account: string;
  guestData: string;
}): void {
  fireWhenIdle(() =>
    emit('auth_completed', {
      entry_context: params.entryContext,
      account: params.account,
      guest_data: params.guestData,
    }),
  );
}

/** The post-auth confirmation toast was shown. `message_kind` is
 *  `account_ready` | `board_saved` (§5). PII-free. */
export function gaAuthConfirmationShown(params: {
  entryContext: string;
  messageKind: string;
}): void {
  fireWhenIdle(() =>
    emit('auth_confirmation_shown', {
      entry_context: params.entryContext,
      message_kind: params.messageKind,
    }),
  );
}

/**
 * OTP sign-in funnel (email → code → done). `surface` is an existing
 * registered dimension — these events spend no new GA4 custom-dimension slot,
 * and carry NO email/code (PII-free per this module's contract).
 *
 * - `otp_requested` — user asked for a code ("Send me a code").
 * - `otp_verified` — a code was accepted and the user is signed in.
 * - `otp_failed`   — a code was rejected (wrong/expired/exhausted). The server
 *   collapses every failure mode to one error, so we don't split by reason.
 */
export function gaOtpRequested(params: { surface: string }): void {
  fireWhenIdle(() => emit('otp_requested', { surface: params.surface }));
}

export function gaOtpVerified(params: { surface: string }): void {
  fireWhenIdle(() => emit('otp_verified', { surface: params.surface }));
}

export function gaOtpFailed(params: { surface: string }): void {
  fireWhenIdle(() => emit('otp_failed', { surface: params.surface }));
}

/**
 * DIAGNOSTIC (identity-loss investigation, 2026-07-21): `ensureAuth` was
 * about to mint a fresh anonymous user while a persisted identity was
 * visibly sitting in localStorage — i.e. the SDK's session restore came up
 * empty with storage intact. This is the precondition for silently
 * orphaning a returning user's boards (the fresh anon overwrites the stored
 * identity), observed live on 2026-07-21 after a same-tab click-out round
 * trip. Counting it in prod is the diagnosis step before any restore-retry
 * fix — do NOT act on this signal in code; measure first.
 *
 * `method` carries the document's navigation type (back_forward / reload /
 * navigate / unknown) so click-out returns are distinguishable — an existing
 * registered GA4 dimension, no new custom-dimension slot spent.
 */
export function gaAnonRestoreMissed(params: { method: string }): void {
  fireWhenIdle(() => emit('anon_restore_missed', { method: params.method }));
}

export function gaFirstRender(): void {
  fireWhenIdle(() =>
    emit('page_first_render', {
      page_path:
        typeof window !== 'undefined'
          ? scrubSensitiveParams(window.location.pathname + window.location.search)
          : '',
    }),
  );
}

export interface GaQuizSearchSubmittedParams {
  occasion?: string;
  relationship?: string;
  age_range?: string;
  interest_count?: number;
  /** Whether the freeform "tell us more" field was filled in. */
  has_freeform?: boolean;
  /** From recipient.gender — `'female' | 'male' | 'other' | undefined`. */
  gender?: string;
  /** Carousel session id (joins to Firestore for the rollup table). */
  carousel_session_id?: string;
  /** Anon user, signed-in user adding a NEW recipient, or a quiz attached to a
   *  recipient who already existed (the no-quiz board's "Get gift ideas"). */
  flow_type?: QuizFlowType;
  entry_point?: QuizEntryPoint;
}

/** Custom event — fired when the quiz submit callable resolves successfully. */
export function gaQuizSearchSubmitted(params: GaQuizSearchSubmittedParams): void {
  fireWhenIdle(() => emit('quiz_search_submitted', { ...params }));
}

export interface GaQuizResultsViewedParams {
  occasion?: string;
  relationship?: string;
  age_range?: string;
  interest_count?: number;
  carousel_count: number;
  product_count: number;
  /** Carousel session id (joins to Firestore). */
  carousel_session_id?: string;
}

/** Custom event — fired once when the results page first lands a COMPLETED session. */
export function gaQuizResultsViewed(params: GaQuizResultsViewedParams): void {
  fireWhenIdle(() => emit('quiz_results_viewed', { ...params }));
}

// --- Carousel impression / scroll ------------------------------------------

export interface GaCarouselVisibleParams {
  /** Display title of the carousel (used as the dashboard segment). */
  carousel_name: string;
  /** Zero-based ordinal of this carousel within the page. */
  carousel_index: number;
  /** Total carousels on the page (denominator for "saw N of M" reports). */
  total_carousels: number;
  /** Card count in this specific carousel. */
  total_cards: number;
  /** On guide pages — the slug. On results pages — undefined. */
  occasion?: string;
  /** On results pages — the carouselSession id. On guide pages — undefined. */
  carousel_session_id?: string;
}

/** Fired once per session per carousel when ≥50% of the carousel is in view. */
export function gaCarouselVisible(params: GaCarouselVisibleParams): void {
  fireWhenIdle(() => emit('carousel_visible', { ...params }));
}

export interface GaCarouselScrollParams extends GaCarouselVisibleParams {
  /** Number of cards the user has revealed via horizontal scroll. */
  cards_visible: number;
  /** Threshold crossed: 25 / 50 / 75 / 100. */
  percent_seen: 25 | 50 | 75 | 100;
}

/**
 * Fired on each 25/50/75/100% horizontal-scroll threshold within a carousel.
 * Throttled (200ms minimum) at the listener; this helper has no rate-limiting.
 */
export function gaCarouselScroll(params: GaCarouselScrollParams): void {
  fireWhenIdle(() => emit('carousel_scroll', { ...params }));
}

// --- Vertical board feed: section (category) impressions ---------------------
// Replaces carousel_visible/carousel_scroll for the post-#171 vertical feed,
// where products are stacked in vertical category sections rather than
// horizontal rows. Firing once per category REACHED also gives scroll depth
// (the deepest section_type reached = how far down the feed the user got).

export type SectionType = 'trending' | 'relationship' | 'date_night' | 'interest_chip';

export interface GaSectionVisibleParams {
  /** The section's key — 'wildcard' | 'sentimental' | 'date_night' | chip label. */
  section_key: string;
  /** Coarse category bucket for join-free funnel analysis across chip labels. */
  section_type: SectionType;
}

/**
 * Fired once per category the user scrolls far enough down the vertical board
 * feed to reach (the section becomes the active/in-view section). Deduped per
 * session by the caller. Register `section_key` / `section_type` as GA4
 * Event-scoped custom dimensions.
 */
export function gaSectionVisible(params: GaSectionVisibleParams): void {
  fireWhenIdle(() => emit('section_visible', { ...params }));
}

// --- Board progress card (THE-946/THE-947, PR 1) ----------------------------
// New events, named once per the spec's build notes — do not add or drop any.
// `stage` is the card's resolved stage.
//
// STAGE 4 WAS ADDED BY THE-963 (PR 6) WITHOUT A NEW EVENT NAME. V1 renders no
// card at stage 4, so the event could not fire there and the union stopped at
// 3. The V2 Fresh Finds Friday card DOES render at stage 4, and it is a view
// of this same card at a resolved stage — so it reports through
// `board_card_view` with `stage: 4` rather than through a second event. The
// epic's rule is that each event is named exactly once; a
// `board_friday_card_view` beside this one would be the duplicate that rule
// forbids. A stage-4 row simply did not exist in any report before V2.

export type BoardProgressCardStage = 1 | 2 | 3 | 4;

export interface GaBoardCardViewParams {
  stage: BoardProgressCardStage;
}

/** Fired when the top card shows, once per resolved stage (including the
 *  first stage a given mount shows, and again whenever it changes). */
export function gaBoardCardView(params: GaBoardCardViewParams): void {
  fireWhenIdle(() => emit('board_card_view', { stage: params.stage }));
}

/** Fired once, the moment the board's count reaches 10 and stage 3 (the
 *  one-time celebration) becomes visible. */
export function gaBoardCelebrationView(): void {
  fireWhenIdle(() => emit('board_celebration_view', {}));
}

export interface GaBoardSavesClickParams {
  /** Saved-gift count at the moment of the click — distinguishes an empty-state
   *  ("Your saves live here") tap from a filled one without a join to any
   *  other event. */
  saved_count: number;
}

/** Fired when the saves indicator in the desktop pinned header (THE-949 /
 *  PR 3) is clicked, opening the recipient's Saved page. Separate from any
 *  other "open Saved page" entry point — this surface is desktop-only and
 *  always visible while scrolling, so it answers a different question than a
 *  generic liked-thumbnail tap. */
export function gaBoardSavesClick(params: GaBoardSavesClickParams): void {
  fireWhenIdle(() => emit('board_saves_click', { saved_count: params.saved_count }));
}

// --- Bottom-of-board "fine-tune her picks" card (THE-1098) -----------------

/** 'default' = the every-other-time state (mock Z0); 'zero' = out of updates
 *  for today (mock Z1, title "Try again tomorrow"). One event with a state
 *  split, mirroring `board_card_view`'s `stage` param, rather than two event
 *  names — both answer "how far did she get, and did she hit the wall?" off
 *  the same shape. */
export type GaBoardBottomCardState = 'default' | 'zero';

export interface GaBoardBottomCardViewParams {
  state: GaBoardBottomCardState;
}

/** Fired once the card is on screen, and again whenever its state flips
 *  between default and zero (a live usage-count update can do that without a
 *  remount) — same "fire on resolve, and on change" rule `gaBoardCardView`
 *  already follows. Answers the write-up's "when a shopper reaches the bottom
 *  card" and "when she sees the out-of-updates state" in one event. */
export function gaBoardBottomCardView(params: GaBoardBottomCardViewParams): void {
  fireWhenIdle(() => emit('board_bottom_card_view', { state: params.state }));
}

/** Which door opened the Update picks sheet. */
export type GaUpdatePicksSource = 'header' | 'interest_rail' | 'profile_nudge' | 'bottom_card';

export interface GaUpdatePicksOpenedParams {
  source: GaUpdatePicksSource;
}

/**
 * The Update picks sheet opened, from ANY door (header pill, interest rail
 * "+", profile card, the THE-1098 bottom card, …). THE-1098 write-up: "nothing
 * records when the Update picks panel opens, from any button" — a gap every
 * existing door already had, not just the new one, so this fires from the
 * single `openUpdatePicks` chokepoint in BoardLayout rather than being added
 * per-door. Takes a `source` (QA review, 2026-10-21: the original no-params
 * shape couldn't tell the doors apart, which is the exact question this event
 * exists to answer) — each call site passes its own door's literal.
 */
export function gaUpdatePicksOpened(params: GaUpdatePicksOpenedParams): void {
  fireWhenIdle(() => emit('update_picks_opened', { source: params.source }));
}

// --- Promotion (banner) -----------------------------------------------------

export interface GaPromotionParams {
  /** Stable identifier for this banner placement. e.g. `md_quiz_cta`. */
  promotion_id: string;
  /** Human-friendly label. e.g. `Mother's Day quiz CTA`. */
  promotion_name: string;
  /** Versioned creative reference. e.g. `mothers_day_banner_v1`. */
  creative_name: string;
  /** Where on the page. e.g. `occasion_mothers_day_mid_carousel`. */
  location_id: string;
}

/** Fired when the banner enters viewport (≥50% visible). */
export function gaViewPromotion(params: GaPromotionParams): void {
  fireWhenIdle(() => emit('view_promotion', { ...params }));
}

/** Fired when the banner CTA is clicked. */
export function gaSelectPromotion(params: GaPromotionParams): void {
  fireWhenIdle(() => emit('select_promotion', { ...params }));
}

// --- Product clicks ---------------------------------------------------------

export interface GaSelectItemParams {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
  currency?: string;
  /**
   * Which app surface the interaction happened on — one value per surface:
   * 'board' | 'liked' | 'shared_board' | 'gift_guide'. Rail ("more like this")
   * entries carry the same surface as card opens and are identified by the
   * similar_product_tap companion event. Lets the dashboard split views/clicks
   * by surface on a single event. Register as a GA4 custom dimension
   * (Event-scoped) or it's silently dropped from reports.
   */
  surface?: string;
  /**
   * Marks a pasted-link item (THE-1084 PR6, spec v2's Measurement section:
   * "on the existing vocabulary" — reuse `select_item` rather than mint a
   * second open-event taxonomy, and mark it instead) so the dashboard can
   * split Thea picks from added links on the same event without the two
   * getting inflated or confused together.
   */
  kind?: 'LINK';
}

/**
 * Standard GA4 select_item — fired when a user clicks a product card to open
 * the affiliate URL. GA4's recommended-event shape uses an `items` array.
 */
export function gaSelectItem(params: GaSelectItemParams): void {
  fireWhenIdle(() => {
    const { item_id, item_name, item_category, price, currency, surface, kind } = params;
    emit('select_item', {
      item_list_name: item_category ?? 'quiz_results',
      ...(surface !== undefined ? { surface } : {}),
      ...(kind !== undefined ? { kind } : {}),
      items: [
        {
          item_id,
          item_name,
          ...(item_category !== undefined ? { item_category } : {}),
          ...(price !== undefined ? { price } : {}),
        },
      ],
      ...(currency !== undefined ? { currency } : {}),
    });
  });
}

export interface GaProductClickParams {
  product_id: string;
  product_name: string;
  brand?: string;
  price?: number;
  destination_url: string;
  /** On guide pages — the slug. */
  occasion?: string;
  /** Carousel/section title, or the surface name on grid surfaces without
   *  sections (e.g. the liked views send 'liked'). Optional since 2026-08-04
   *  so those surfaces can fire click parity without a fake position. */
  carousel_name?: string;
  /** Zero-based card position within the carousel. Absent on grid surfaces
   *  where the click comes from a PDP sheet, not a positioned card. */
  card_position?: number;
  /**
   * Which app surface the click happened on — e.g. 'gift_guide', 'quiz_results'.
   * The dashboard already separates guide vs result clicks by event name
   * (`product_click` vs `quiz_results_product_click`); `surface` is the
   * finer-grained, single-event split. Register as a GA4 custom dimension
   * (Event-scoped) to query it.
   */
  surface?: string;
  /**
   * Marks a pasted-link click-out so the dashboard can split Thea picks
   * from added links on the same event (see `GaSelectItemParams.kind`).
   * Callers already spread this in ahead of the type carrying it
   * (SharedBoardView/LikedPage/AllSavedPage's merchant click-out, THE-1115)
   * — declaring it here closes that type hole rather than changing behavior.
   */
  kind?: 'LINK';
}

/**
 * Fired alongside `select_item` for guide-page clicks (occasion-page CTR
 * reporting needs the v6 dashboard event name). On the results page, the
 * companion event is `quiz_results_product_click` (Tier 1b).
 */
export function gaProductClick(params: GaProductClickParams): void {
  fireWhenIdle(() => emit('product_click', { ...params }));
}

// --- "More like this" rail (NEW post-#171; didn't exist before) --------------

export interface GaSimilarProductTapParams {
  /** The similar item that was tapped. */
  product_id: string;
  /** The product whose rail it was (what the recommendation grew from). */
  seed_product_id?: string;
  /** The original card/product that started the drill-down chain (stack root). */
  source_product_id?: string;
  /** board | liked | shared_board | gift_guide */
  surface: string;
}

/**
 * Fired when a user taps a "more like this" rail tile. The rail is new with the
 * vertical PDP, so this is net-new signal: which similar items get tapped, and —
 * via `seed_product_id` / `source_product_id` — which original item drove the
 * exploration. Register seed_product_id / source_product_id / surface as GA4
 * Event-scoped custom dimensions.
 */
export function gaSimilarProductTap(params: GaSimilarProductTapParams): void {
  fireWhenIdle(() => emit('similar_product_tap', { ...params }));
}

export interface GaSimilarRailEngagedParams {
  /** The product whose "more like this" rail was swiped. */
  seed_product_id: string;
  /** board | liked | shared_board | gift_guide */
  surface: string;
}

/**
 * Fired once per PDP-open when the user horizontally SWIPES the "more like this"
 * rail (vs just tapping a tile, which is `similar_product_tap`). The signal that
 * answers "is the rail — suspected too hidden — actually noticed and explored?":
 * compare rail-engaged against PDP-opens. Register seed_product_id / surface as
 * GA4 Event-scoped custom dimensions.
 */
export function gaSimilarRailEngaged(params: GaSimilarRailEngagedParams): void {
  fireWhenIdle(() => emit('similar_rail_engaged', { ...params }));
}

// --- Homepage tile ----------------------------------------------------------

export interface GaOccasionCardClickParams {
  occasion: string;
  /** Which surface the card was tapped ON — 'homepage_rail' for the
   *  logged-out homepage's "Trending gift guides" rail. Omitted on
   *  /gift-guides, whose cards keep firing exactly as they do today. Splits a
   *  shared event by an added dimension rather than minting a new `surface`
   *  value (that vocabulary is fixed) — the same move guide_quiz_cta makes
   *  with `action`. Register as a GA4 Event-scoped custom dimension. */
  entry_point?: string;
}

/**
 * Fired when a tile in the homepage occasion grid is clicked.
 *
 * Deliberately NOT wrapped in fireWhenIdle: the OccasionTile renders as a
 * plain `<a href>` which triggers a same-tab hard navigation. A deferred
 * fire risks executing AFTER the page begins unloading — at which point
 * `window.gtag` may already be torn down and the event is lost. The cost of
 * firing inline is a single ~0.1ms gtag call on the main thread, which is
 * imperceptible; the alternative (lost events) is much worse for the
 * dashboard's homepage-tile CTR metric.
 */
export function gaOccasionCardClick(params: GaOccasionCardClickParams): void {
  emit('occasion_card_click', { ...params });
}

// --- Gift-guide quiz CTAs ----------------------------------------------------

export interface GaGuideQuizCtaParams {
  /** Which placement was tapped: the always-reachable sticky footer bar, the
   *  end-of-list clay banner, or the save picker's zero-people empty state.
   *  Reuses the registered `action` dimension — new placements add a value
   *  here, never a new event name. */
  action: 'sticky_bar' | 'end_banner' | 'save_picker';
  /** The guide's route slug (mirrors product_click's occasion tagging). */
  occasion?: string;
  surface: 'gift_guide';
}

/**
 * Fired when a guide visitor taps a "Get started" quiz CTA on a gift-guide
 * page. Answers "does the sticky bar or the end banner drive quiz starts?"
 * without new GA4 custom dimensions — `action`, `occasion`, and `surface` are
 * all already registered. NOT wrapped in fireWhenIdle: the tap navigates to
 * /quiz via the router, so fire inline like the other navigation CTAs.
 */
export function gaGuideQuizCta(params: GaGuideQuizCtaParams): void {
  emit('guide_quiz_cta', { ...params });
}

export interface GaGuideCardClickParams {
  /** The curated guide's route slug (e.g. 'lake-house') — mirrors
   *  guide_quiz_cta's occasion tagging. */
  occasion: string;
  surface: 'gift_guide' | 'gift_guide_hub';
  /** Which surface the card was tapped ON — see GaOccasionCardClickParams. */
  entry_point?: string;
}

/**
 * Fired when a curated-guide card on the /gift-guides browse tab is tapped.
 * Occasion cards on the same tab keep firing occasion_card_click (dashboard
 * parity with the old tile grid). `occasion` and `surface` are both already
 * registered — no new GA4 custom dimensions. Fires inline: the tap is an
 * in-SPA router navigation, same as the other navigation CTAs.
 */
export function gaGuideCardClick(params: GaGuideCardClickParams): void {
  emit('guide_card_click', { ...params });
}

export interface GaDiveDeeperOpenParams {
  /** The board section's own "Dive deeper into [topic]" topic string, e.g.
   *  'cocktails' or 'gifts for moms' — see diveDeeperHeading.ts. NOT the
   *  section's raw chip key, so this reads the same in GA as the heading a
   *  person actually tapped. */
  topic: string;
  /** The guide's key (occasion/slug), matching every other guide event's
   *  `occasion` vocabulary — named `guide` here only because the board spec
   *  (THE-950) names the event's own param that. */
  guide: string;
}

/**
 * Fired when a card in the board's per-section "Dive deeper into [topic]"
 * block is opened (THE-950 PRD build notes: "dive_deeper_open (with topic
 * and guide)"). A new event, not a guide_card_click variant: the rail opens
 * the guide as a sheet/pop-up over the board rather than navigating, which is
 * a different action on a different surface from every other guide-card tap.
 */
export function gaDiveDeeperOpen(params: GaDiveDeeperOpenParams): void {
  emit('dive_deeper_open', { ...params });
}

export interface GaGuideSeeAllClickParams {
  /** Which surface's "See all" was tapped — 'homepage_rail' today. */
  entry_point: string;
}

/**
 * Fired when the "See all" affordance beside a guide rail's heading is tapped.
 * Separate from guide_card_click because it answers a different question: the
 * card events measure interest in ONE guide, this one measures appetite for
 * the whole library — the signal that says whether the rail is working as a
 * door to /gift-guides rather than just a shortcut to three guides. Register
 * `guide_see_all_click` as a GA4 event and `entry_point` as an Event-scoped
 * custom dimension. Fires inline: the tap is an in-SPA router navigation, same
 * as the other navigation CTAs.
 */
export function gaGuideSeeAllClick(params: GaGuideSeeAllClickParams): void {
  emit('guide_see_all_click', { ...params });
}

// --- Calendar / Coming Up (occasion engine engagement) ----------------------

/**
 * The ONLY value `event_type` may take for a holiday card, on either surface.
 *
 * THE-746 plan revision 4, board correction: the generic string is allowed so
 * tap counts survive, but no specific holiday identifier, name or preference
 * may reach GA4. Exported as a constant — rather than written inline at each
 * fire site — so the privacy contract is one symbol that both this surface
 * (PR 2.4) and the calendar's EventCard (PR 3.4 / THE-765) spend, and so a
 * test can assert the payload rather than the absence of a call.
 *
 * Passing a real holiday `eventType` here ('hanukkah', 'christmas', …) is the
 * leak. There is deliberately no second allowed value and no suffix scheme:
 * every holiday is `'holiday'`, which is what makes the dimension unable to
 * carry a choice.
 */
export const GA_HOLIDAY_EVENT_TYPE = 'holiday';

export interface GaCalendarEventTapParams {
  /** Where the tap happened: 'coming_up_strip' (homepage) or 'calendar'. */
  surface: 'coming_up_strip' | 'calendar';
  /** birthday / anniversary — or, for any holiday card,
   *  `GA_HOLIDAY_EVENT_TYPE` and never the holiday's own type. */
  event_type: string;
  /**
   * Days from today to the event (only known on the Coming Up strip).
   *
   * MUST BE OMITTED ON HOLIDAY CARDS. GA4 timestamps every hit, so
   * `{event_type: 'holiday', days_until: 14}` plus the hit time names the
   * exact date, and a holiday's date names the holiday — the identifier the
   * generic `event_type` exists to withhold, arriving by arithmetic. Person
   * cards keep it: a birthday's date is already the recipient's, and that
   * dimension is shipped, registered telemetry this PR does not change.
   */
  days_until?: number;
  /**
   * Where the tap actually landed (THE-1403): `'board'` | `'guide'` |
   * `'collection'` | `'occasion'` | `'calendar'` (stayed on the Calendar —
   * no gift page exists yet). A coarse KIND, never a slug/collection key/
   * recipient id — those would re-identify the holiday or person the
   * `event_type`/`days_until` privacy rules above withhold. Optional because
   * the Calendar's own EventCard fire sites don't compute it (they already
   * know their destination a beat later, inside `handleCardTap`); every
   * Coming Up strip fire site passes it.
   */
  destination?: 'board' | 'guide' | 'collection' | 'occasion' | 'calendar';
}

/**
 * Fired when a user taps an upcoming-occasion card — the homepage "Coming up"
 * strip (which navigates straight to the recipient's board or the holiday's
 * gift page) or an event in the calendar. Answers "does anyone engage with
 * the occasion engine, and does it drive people into a board?". NOT wrapped
 * in fireWhenIdle: the Coming Up card is a router `<Link>`, so fire inline to
 * avoid losing the event to the navigation. Register `surface` / `event_type`
 * / `days_until` / `destination` as GA4 Event-scoped custom dimensions.
 */
export function gaCalendarEventTap(params: GaCalendarEventTapParams): void {
  emit('calendar_event_tap', { ...params });
}

/** Hub desktop menu opening; no account or notification data is accepted. */
export function gaMenuOpened(): void {
  emit('menu_opened', { surface: 'hub_desktop' });
}

// --- Alerts (notification bell) ---------------------------------------------

export interface GaAlertActionParams {
  /** 'click' = acted on the alert (navigated to its target); 'dismiss' = ×;
   *  'expand' = toggled the birthday rollup row open/closed. */
  action: 'click' | 'dismiss' | 'expand';
  /** add_birthday / coming_up_soon / bought_any / … */
  alert_type: string;
  /** Fixed copy variant, never the rendered headline or recipient name. */
  wording_shown?: 'add_birthday' | 'get_gift_ideas' | 'standard';
}

/**
 * Fired when a user acts on an alert row — clicking it (navigates to the
 * recipient's board) or dismissing it (×). Answers "do alerts get acted on or
 * just dismissed?". NOT wrapped in fireWhenIdle: the click path does a hard
 * `window.location.assign`, so fire inline to avoid losing the event. Register
 * `action` / `alert_type` / `wording_shown` as GA4 Event-scoped custom dimensions.
 */
export function gaAlertAction(params: GaAlertActionParams): void {
  const { action, alert_type, wording_shown } = params;
  emit('alert_action', { action, alert_type, ...(wording_shown ? { wording_shown } : {}) });
}

// --- Save / dismiss / regenerate (Tier 1b) ---------------------------------

/**
 * Payload for `product_saved`. (Previously shared with `product_dismissed`,
 * whose X-dismiss UI was removed in #126 — the helper was retired in the
 * 2026-08-04 parity audit.) The full quiz-input cohort fields are duplicated
 * on every event so the dashboard can group save rate by occasion /
 * relationship / age / gender without joining against the carouselSession
 * doc. The `regenerate_count` lets us answer "are users more likely to save
 * after refreshing their picks?"
 */
export interface GaProductReactionParams {
  product_id: string;
  product_name: string;
  brand?: string;
  price?: number;
  /** Carousel section title the product was in. */
  carousel_name: string;
  /** Zero-based card position within the carousel. */
  card_position: number;
  /** Recipient + occasion cohort fields (from the recommendation doc). */
  relationship?: string;
  occasion?: string;
  gender?: string;
  age_range?: string;
  /** Number of times the user has regenerated picks in this session. 0 if
   *  they're acting on the original results, 1+ after a refresh. */
  regenerate_count: number;
  /** Carousel session id (joins to Firestore for the rollup). */
  carousel_session_id?: string;
  /** Surface tag for saves outside the results page (e.g. 'gift_guide').
   *  Absent on results-page saves — the un-tagged historical default. */
  surface?: string;
  /** Guide picker saves fan out to N people in one confirm; the results
   *  page can only ever save to its one recipient (never sent there). */
  recipient_count?: number;
}

/** Fired when the user hearts a product on the results page. */
export function gaProductSaved(params: GaProductReactionParams): void {
  fireWhenIdle(() => emit('product_saved', { ...params }));
}

/** Source of a regenerate request. Live paths on the board today:
 *  `search_pill_update` (occasion/freeform edit in the pill sheet) and
 *  `about_sheet_age_change` (age band edit in the About sheet). The drawer
 *  and sticky-button paths predate the vertical feed and are retained for
 *  dashboard continuity. */
export type RegeneratePath =
  | 'search_pill_update'
  | 'about_sheet_age_change'
  | 'update_picks_from_drawer'
  | 'refresh_my_picks_button'
  /** User-initiated recovery from a board whose pipeline never finished
   *  (THE-500). Worth splitting out on the dashboard: its volume is a direct
   *  read on how often the carousel kick-off is being lost in the field. */
  | 'stalled_board_retry'
  /** "Refresh <Name>'s board" in the end-of-board "Improve [Name]'s profile"
   *  module (THE-517). Split from `refresh_my_picks_button` (the legacy sticky
   *  button) on purpose: that module is a v1 shipped to find out whether the
   *  prompt works, so its regenerate volume has to be readable on its own. */
  | 'profile_nudge_refresh'
  /** "Update picks" from the bottom-of-board "fine-tune her picks" card
   *  (THE-1098). Split out so the card's own regenerate volume is readable —
   *  every other path above already answers "which OTHER door", so a refresh
   *  the card actually drove would otherwise fold into `search_pill_update`
   *  and look identical to a header-pill edit. */
  | 'board_bottom_card';

export interface GaRegenerateRecommendationsParams {
  path: RegeneratePath;
  relationship?: string;
  occasion?: string;
  /** Number of carousels in the prior result (denominator for "how much
   *  picks-set changed" analyses). */
  prior_carousel_count: number;
  /** Carousel session id of the recommendation that's being regenerated. */
  carousel_session_id?: string;
}

/**
 * Fired when the user triggers a full regenerate (pill "Update picks" with an
 * occasion/freeform change, About-sheet age change). Just the trigger event — `_completed` and `_failed` companion events are deferred to
 * Tier 2 (they need funnel-completion analysis we don't have a dashboard for
 * yet). Click-only is enough to denominate the "did this user enter a
 * regenerated state" cohort for save/dismiss-rate comparisons.
 */
export function gaRegenerateRecommendations(
  params: GaRegenerateRecommendationsParams,
): void {
  fireWhenIdle(() => emit('regenerate_recommendations', { ...params }));
}

// --- Board reserve pool (THE-514) ------------------------------------------

export interface GaBoardReserveLowParams {
  /** Products still held back and available to backfill a removal. */
  reserve_count: number;
  /** Products currently rendered on the board (the ~40 cap). */
  displayed_count: number;
  /** Threshold that was crossed — sent so a later tuning of the constant
   *  stays legible in the historical data. */
  threshold: number;
  /** Carousel session id, joining this to the board's generation cohort. */
  carousel_session_id?: string;
}

/**
 * Fired on the crossing into low reserve: the board's surplus of already-
 * generated products has run down far enough that the next few removals will
 * shrink the board instead of backfilling it (THE-514).
 *
 * Once per crossing, not once per render, and re-armed if the reserve recovers
 * — a regenerate or an undo can both do that. This event is the observability
 * half of the signal; THE-510 item #3's "Refresh his board" module is the
 * user-facing half, and reads the same `isReserveLow` flag out of
 * `useBoardFeedCap`.
 *
 * Volume here is a direct read on whether the reserve behind the 40-cap is
 * deep enough in the field, which is the main open question about capping at
 * display time rather than in the backend.
 */
export function gaBoardReserveLow(params: GaBoardReserveLowParams): void {
  fireWhenIdle(() => emit('board_reserve_low', { ...params }));
}

// --- Results-page product click (v6 dashboard parity) ----------------------

export interface GaQuizResultsProductClickParams {
  product_id: string;
  product_name: string;
  brand?: string;
  price?: number;
  destination_url: string;
  carousel_name: string;
  card_position: number;
  /** Recipient + occasion fields from the recommendation. */
  relationship?: string;
  occasion?: string;
  /** Carousel session id (joins to Firestore + ties click back to the
   *  specific recommendation cohort the dashboard analyses). */
  carousel_session_id?: string;
}

/**
 * Fired alongside the existing `select_item` (kept for GA4 Enhanced Ecommerce)
 * when a user clicks a product on the results page. The v6 dashboard's SQL
 * queries by event name `quiz_results_product_click`; this event is the
 * dashboard-parity companion. See spec §11.11 (decision 12.1).
 */
export function gaQuizResultsProductClick(
  params: GaQuizResultsProductClickParams,
): void {
  fireWhenIdle(() => emit('quiz_results_product_click', { ...params }));
}

// --- Time to first result (LCP-anchored, per §14) --------------------------

export interface GaTimeToFirstResultParams {
  /** T(first paint of the ready board) − T(submit click). The headline metric
   *  Kate cares about. */
  time_to_first_result_ms: number;
  /** Sub-timing: T(submitGiftFlow callable resolves) − T(submit click). */
  submit_callable_ms: number;
  /** Sub-timing: T(carouselSession === COMPLETED) − T(submit-callable resolve). */
  agent_phase_ms: number;
  /** Sub-timing: T(first paint of the ready board) − T(carouselSession ===
   *  COMPLETED) — the FE render-to-paint cost. Replaces the pre-2026-08-17
   *  `nav_to_lcp_ms`, which was structurally 0 (see useTimeToFirstResult). */
  ready_to_paint_ms: number;
  /** Cohort fields. */
  occasion?: string;
  relationship?: string;
  /** Carousel session id (joins to Firestore for the rollup). */
  carousel_session_id?: string;
}

/**
 * Fired exactly once per recommendation — when the browser has painted the
 * first frame of the ready results board. The sub-timings split the total
 * felt latency into BE submit, agent, and FE-render-to-paint phases so
 * optimization effort can target the biggest contributor. See spec §14.
 *
 * NOTE: anchored to the paint AFTER the FE's "ready to render" commit, not
 * to LCP (LCP is finalized at first input, so an SPA results page never gets
 * one — rows before 2026-08-17 carry `time_to_first_result_ms: 0` for that
 * reason and should be excluded from dashboards). `quiz_results_viewed`
 * fires on the ready decision itself; the two together separate "we thought
 * the page was ready" from "the user actually saw content."
 */
export function gaTimeToFirstResult(params: GaTimeToFirstResultParams): void {
  fireWhenIdle(() => emit('time_to_first_result_ms', { ...params }));
}

// --- Share (board sharing / viral loop) -------------------------------------

export interface GaShareInitiatedParams {
  /** The recipient whose board is being shared. */
  recipient_id: string;
  /**
   * Which saved-tray surface the share was initiated from (THE-1139).
   * `<ShareButton>`/`<ShareListButton>` are both rendered from the one
   * shared `BoardSavedPanel`, so without this a board share and a
   * liked/all-saved-page share are indistinguishable in GA4. Optional —
   * callers outside `BoardSavedPanel` (if any) still compile unchanged.
   */
  surface?: 'board' | 'liked';
}

/**
 * Custom event — fired the moment the Share button is tapped (intent), BEFORE
 * the link is minted or the share sheet opens. Pairs with `share` (completion)
 * so the dashboard can measure share-sheet abandonment: initiated − completed.
 */
export function gaShareInitiated(params: GaShareInitiatedParams): void {
  fireWhenIdle(() => emit('share_initiated', { ...params }));
}

export type ShareMethod = 'native' | 'copy';

export interface GaShareParams {
  /** 'native' = OS share sheet succeeded; 'copy' = clipboard fallback. */
  method: ShareMethod;
  recipient_id: string;
  /** Same surface split as `GaShareInitiatedParams.surface` (THE-1139). */
  surface?: 'board' | 'liked';
}

/**
 * GA4 recommended `share` event — fired on successful completion (the native
 * share sheet resolved, or the copy-link fallback wrote to the clipboard). NOT
 * fired when the user cancels the share sheet (AbortError). `method` +
 * `content_type` are GA4-recommended params; `method` may already be a
 * registered dimension (sign_up uses it) — verify it covers the share event.
 */
export function gaShare(params: GaShareParams): void {
  const { method, recipient_id, surface } = params;
  fireWhenIdle(() =>
    emit('share', {
      method,
      content_type: 'board',
      item_id: recipient_id,
      ...(surface !== undefined ? { surface } : {}),
    }),
  );
}

// --- Add a link (THE-1084 PR6) ----------------------------------------------
//
// GA4 measurement for the pasted-link journey (spec v2's Measurement
// section — still marked *(proposed)*; confirm these event names with Kate
// before merge if that's still open). A saved link's own *PDP* "opened" and
// "click-out" reuse the existing product vocabulary marked with `kind:
// 'LINK'` (`GaSelectItemParams`/`GaProductClickParams` above) per the spec's
// "on the existing vocabulary" instruction, so they're not repeated here —
// that's a different moment from `gaAddLinkOpened` below, which marks the
// "+" tile being tapped to open the paste-a-link sheet, before any link
// exists to reuse that vocabulary for. Saved, save-failed, duplicate and
// removed have no existing GA4 event to mark — Firestore writes on every
// other surface in this app don't visibly fail or dedupe the way a pasted
// link can, so there's nothing to reuse — and get their own minimal events
// instead.

export type GaAddLinkSurface = 'liked' | 'shared_board' | 'board';

export interface GaAddLinkParams {
  /** Matches the surface tagging used everywhere else in this file (see
   *  `GaSelectItemParams.surface`). */
  surface: GaAddLinkSurface;
}

/** Fired when the Add-a-link tile is tapped and the paste-a-link sheet opens. */
export function gaAddLinkOpened(params: GaAddLinkParams): void {
  fireWhenIdle(() => emit('add_link_opened', { ...params }));
}

export interface GaAddLinkSavedParams extends GaAddLinkParams {
  /** Whether the optional Name field was filled in — the spec's "link saved
   *  (named vs not)" split. */
  named: boolean;
}

/** Fired on a successful Add-a-link save (AddLinkSheet, no duplicate/failure). */
export function gaAddLinkSaved(params: GaAddLinkSavedParams): void {
  fireWhenIdle(() => emit('add_link_saved', { ...params }));
}

/** Fired when `recordAddedLink` rejects or the request fails/offline. */
export function gaAddLinkSaveFailed(params: GaAddLinkParams): void {
  fireWhenIdle(() => emit('add_link_save_failed', { ...params }));
}

export interface GaAddLinkDuplicateParams extends GaAddLinkParams {
  /** Which duplicate copy showed — "Already saved" vs "Already bought". */
  previous_state: 'SAVED' | 'PURCHASED';
}

/** Fired when a pasted link hashes to an already-SAVED/PURCHASED doc. */
export function gaAddLinkDuplicate(params: GaAddLinkDuplicateParams): void {
  fireWhenIdle(() => emit('add_link_duplicate', { ...params }));
}

/** Fired when a saved pasted link is removed (un-liked). */
export function gaAddLinkRemoved(params: GaAddLinkParams): void {
  fireWhenIdle(() => emit('add_link_removed', { ...params }));
}

/** Desktop calendar telemetry: no dates, recipient IDs, or holiday identifiers. */
export function gaCalendarDayClick(params: { event_count: number }): void {
  emit('calendar_day_click', { ...params });
}

// Account settings events deliberately accept only fixed surface labels. Never
// spread caller data: addresses, account IDs and holiday choices do not belong here.
export type GaAccountSurface = 'settings' | 'you';
export function gaSettingsOpened({ surface }: { surface: GaAccountSurface }): void {
  fireWhenIdle(() => emit('settings_opened', { surface }));
}
export function gaPasswordResetSent({ surface }: { surface: GaAccountSurface }): void {
  fireWhenIdle(() => emit('password_reset_sent', { surface }));
}
export function gaEmailChangeStarted({ surface }: { surface: GaAccountSurface }): void {
  fireWhenIdle(() => emit('email_change_started', { surface }));
}
